import React, { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';

const AdminNotes = ({ user, db, setModalMessage, onShowNotesModal }) => {
    const [notes, setNotes] = useState([]);

    // Fetch notes from Firestore
    useEffect(() => {
        if (!db) return;

        const notesCollectionRef = collection(db, 'adminNotes');
        const q = query(notesCollectionRef, orderBy('lastModified', 'desc')); // Order by last modified

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedNotes = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate().toLocaleString(),
                lastModified: doc.data().lastModified?.toDate().toLocaleString()
            }));
            setNotes(fetchedNotes);
        }, (error) => {
            console.error("Error fetching admin notes:", error);
            setModalMessage(`Error loading notes: ${error.message}`);
        });

        return () => unsubscribe();
    }, [db, setModalMessage]);

    const handleCreateNote = () => {
        onShowNotesModal(); // Use prop-based modal opening
    };

    const handleEditNote = useCallback((note) => {
        onShowNotesModal(note); // Pass note data to prop-based modal
    }, [onShowNotesModal]);

    const handleDeleteNote = useCallback(async (noteId, noteTitle) => {
        if (!db) return;

        // Using CustomModal for confirmation instead of window.confirm
        const confirmDelete = await new Promise(resolve => {
            setModalMessage({
                text: `Are you sure you want to delete note "${noteTitle}"? This action cannot be undone.`,
                confirm: true,
                onConfirm: () => { resolve(true); setModalMessage(null); },
                onCancel: () => { resolve(false); setModalMessage(null); }
            });
        });

        if (!confirmDelete) return;

        try {
            const noteDocRef = doc(db, 'adminNotes', noteId);
            await deleteDoc(noteDocRef);
            setModalMessage(`Note "${noteTitle}" deleted successfully!`);
        } catch (error) {
            console.error("Error deleting note:", error);
            setModalMessage(`Failed to delete note: ${error.message}`);
        }
    }, [db, setModalMessage]);

    // Custom modal for confirmation (reusing CustomModal from App.js if it supports confirm)
    // If not, you might need a dedicated confirmation modal here or modify CustomModal in App.js
    // For now, assuming setModalMessage can handle the confirmation object as designed.

    return (
        <div className="glass-card rounded-2xl p-8 h-full flex flex-col relative overflow-hidden">
            {/* Animated Background Effects */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-yellow-500/10 to-orange-500/10 rounded-full blur-3xl opacity-50 animate-pulse-slow"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl opacity-30 animate-float"></div>
            
            {/* Header with Ultra Hot Design */}
            <div className="flex items-center justify-between mb-8 relative z-10">
                <div className="flex items-center group">
                    <div className="relative mr-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/30 to-orange-500/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all animate-pulse"></div>
                        <div className="relative w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-yellow-400/30 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                            <i className="fas fa-sticky-note text-2xl text-yellow-400 animate-float transition-all duration-500 group-hover:text-yellow-300"></i>
                        </div>
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-orange-400 to-red-400 rounded-full animate-bounce"></div>
                    </div>
                    <div className="transition-all duration-300 group-hover:translate-x-2">
                        <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-4xl font-bold gradient-text animate-fadeIn">Admin Notes</h3>
                            <div className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-semibold animate-pulse">
                                {notes.length} Notes
                            </div>
                        </div>
                        <p className="text-gray-400 text-lg animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                            Capture ideas, manage documentation with style ✨
                        </p>
                        <div className="h-1 w-0 bg-gradient-to-r from-yellow-400 to-orange-400 animate-[width-expand_2s_ease-out_0.5s_forwards] rounded-full mt-2"></div>
                    </div>
                </div>
                
                <button
                    onClick={handleCreateNote}
                    className="group relative px-8 py-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 border border-yellow-500/30 hover:border-yellow-400/50 backdrop-blur-xl text-white font-bold rounded-2xl transition-all duration-500 transform hover:scale-110 hover:-translate-y-2 hover:rotate-1 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/25 animate-glow overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                    <div className="relative z-10 flex items-center space-x-3">
                        <div className="w-6 h-6 bg-yellow-400/20 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:rotate-180 group-hover:scale-125">
                            <i className="fas fa-plus text-yellow-400 group-hover:text-yellow-300"></i>
                        </div>
                        <span className="text-lg">Create Note</span>
                        <div className="flex space-x-1">
                            <div className="w-1 h-1 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0s' }}></div>
                            <div className="w-1 h-1 bg-orange-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-1 h-1 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                    </div>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mb-8 relative z-10">
                {[
                    { label: 'Total Notes', value: notes.length, icon: 'fas fa-file-alt', color: 'blue', delay: '0.1s' },
                    { label: 'Recent Notes', value: notes.filter(note => {
                        const noteDate = new Date(note.lastModified);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return noteDate > weekAgo;
                    }).length, icon: 'fas fa-clock', color: 'green', delay: '0.3s' },
                    { label: 'Categories', value: [...new Set(notes.map(note => note.category || 'general'))].length, icon: 'fas fa-tags', color: 'purple', delay: '0.5s' }
                ].map((stat, index) => (
                    <div key={index} className={`group glass-card rounded-xl p-4 hover:scale-105 transition-all duration-500 transform hover:-translate-y-1 hover:shadow-xl hover:shadow-${stat.color}-500/20 animate-fadeIn cursor-pointer border border-white/10 hover:border-${stat.color}-400/30 relative overflow-hidden`} style={{ animationDelay: stat.delay }}>
                        <div className={`absolute inset-0 bg-gradient-to-r from-${stat.color}-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-2">
                                <div className={`w-10 h-10 bg-${stat.color}-500/20 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-12`}>
                                    <i className={`${stat.icon} text-${stat.color}-400 group-hover:text-${stat.color}-300 transition-colors duration-300`}></i>
                                </div>
                                <div className={`text-2xl font-bold text-${stat.color}-400 group-hover:text-${stat.color}-300 transition-colors duration-300 animate-pulse`}>
                                    {stat.value}
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-300">{stat.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Notes Grid with Ultra Hot Design */}
            <div className="flex-grow overflow-y-auto custom-scrollbar pr-2 relative z-10">
                {notes.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center animate-fadeIn">
                            <div className="relative mb-8">
                                <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full blur-2xl animate-pulse"></div>
                                <div className="relative w-32 h-32 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-full flex items-center justify-center mx-auto border border-yellow-500/20">
                                    <i className="fas fa-sticky-note text-6xl text-yellow-400 animate-float"></i>
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center animate-bounce">
                                    <i className="fas fa-plus text-white text-sm"></i>
                                </div>
                            </div>
                            <h3 className="text-3xl font-bold text-white mb-4 animate-pulse">No Notes Yet</h3>
                            <p className="text-gray-400 text-lg mb-6 max-w-md mx-auto">
                                Start capturing your brilliant ideas and important information with our epic note system! 🚀
                            </p>
                            <div className="flex justify-center space-x-2">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
                                <div className="w-2 h-2 bg-orange-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }}></div>
                                <div className="w-2 h-2 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {notes.map((note, index) => (
                            <div
                                key={note.id}
                                className="group glass-card rounded-3xl p-8 hover:scale-[1.02] transition-all duration-700 transform hover:-translate-y-3 hover:shadow-2xl hover:shadow-yellow-500/25 animate-fadeIn cursor-pointer border border-white/10 hover:border-yellow-400/40 relative overflow-hidden min-h-[320px]"
                                style={{ animationDelay: `${index * 0.15}s` }}
                            >
                                {/* Animated Background Gradients */}
                                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/8 via-orange-500/6 to-red-500/8 opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1200"></div>
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-l from-yellow-400/10 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                
                                {/* Note Header with Better Layout */}
                                <div className="relative z-10 mb-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-start space-x-4 flex-1 min-w-0">
                                            <div className="relative flex-shrink-0">
                                                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 border-2 border-yellow-500/30 hover:border-yellow-400/50 shadow-lg group-hover:shadow-yellow-500/25">
                                                    <i className="fas fa-file-alt text-2xl text-yellow-400 group-hover:text-yellow-300 transition-colors duration-300"></i>
                                                </div>
                                                {note.category && (
                                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full text-xs flex items-center justify-center text-white font-bold animate-pulse border-2 border-white/20">
                                                        {note.category.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                {/* Full Title - No Truncation */}
                                                <h4 className="text-2xl font-bold text-white group-hover:text-yellow-300 transition-all duration-300 transform group-hover:translate-x-1 leading-tight mb-3 break-words">
                                                    {note.title}
                                                </h4>
                                                
                                                {/* Priority and Status Row */}
                                                <div className="flex items-center space-x-3 mb-3">
                                                    {note.priority && (
                                                        <div className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 ${
                                                            note.priority === 'high' ? 'bg-red-500/25 text-red-300 border border-red-400/30' :
                                                            note.priority === 'medium' ? 'bg-yellow-500/25 text-yellow-300 border border-yellow-400/30' :
                                                            'bg-green-500/25 text-green-300 border border-green-400/30'
                                                        } animate-pulse`}>
                                                            <i className={`mr-1 ${
                                                                note.priority === 'high' ? 'fas fa-exclamation-triangle' :
                                                                note.priority === 'medium' ? 'fas fa-minus-circle' :
                                                                'fas fa-check-circle'
                                                            }`}></i>
                                                            {note.priority.charAt(0).toUpperCase() + note.priority.slice(1)}
                                                        </div>
                                                    )}
                                                    {note.isPrivate && (
                                                        <div className="px-3 py-1.5 bg-purple-500/25 text-purple-300 rounded-full text-sm font-semibold border border-purple-400/30 animate-pulse">
                                                            <i className="fas fa-lock mr-1"></i>
                                                            Private
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Action Buttons - Always Visible on Larger Cards */}
                                        <div className="flex flex-col space-y-3 ml-4 transition-all duration-300">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditNote(note);
                                                }}
                                                className="p-3 bg-gradient-to-r from-blue-500/25 to-cyan-500/25 hover:from-blue-500/40 hover:to-cyan-500/40 text-blue-300 hover:text-blue-200 rounded-xl transition-all duration-300 transform hover:scale-110 hover:rotate-6 border border-blue-500/40 hover:border-blue-400/60 hover:shadow-lg hover:shadow-blue-500/30 group-hover:animate-pulse"
                                                title="Edit Note"
                                            >
                                                <i className="fas fa-edit text-lg"></i>
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteNote(note.id, note.title);
                                                }}
                                                className="p-3 bg-gradient-to-r from-red-500/25 to-pink-500/25 hover:from-red-500/40 hover:to-pink-500/40 text-red-300 hover:text-red-200 rounded-xl transition-all duration-300 transform hover:scale-110 hover:rotate-6 border border-red-500/40 hover:border-red-400/60 hover:shadow-lg hover:shadow-red-500/30 group-hover:animate-pulse"
                                                title="Delete Note"
                                            >
                                                <i className="fas fa-trash-alt text-lg"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Enhanced Content Preview */}
                                <div className="relative z-10 mb-6">
                                    <div
                                        className="text-gray-300 text-base mb-4 prose prose-invert max-w-none line-clamp-6 overflow-hidden group-hover:text-gray-200 transition-colors duration-300 leading-relaxed"
                                        dangerouslySetInnerHTML={{ 
                                            __html: note.content ? 
                                                (note.content.length > 300 ? note.content.substring(0, 300) + '...' : note.content) 
                                                : '<p class="text-gray-500 italic text-center py-4">No content available</p>' 
                                        }}
                                    />
                                    
                                    {/* Enhanced Tags Section */}
                                    {note.tags && note.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {note.tags.slice(0, 4).map((tag, tagIndex) => (
                                                <span
                                                    key={tagIndex}
                                                    className="px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 text-purple-300 rounded-xl text-sm font-medium border border-purple-500/30 hover:bg-purple-500/30 hover:border-purple-400/50 transition-all duration-300 transform hover:scale-105 hover:-translate-y-0.5 cursor-default"
                                                >
                                                    <i className="fas fa-hashtag mr-1 text-xs"></i>
                                                    {tag}
                                                </span>
                                            ))}
                                            {note.tags.length > 4 && (
                                                <span className="px-3 py-1.5 bg-gray-500/20 text-gray-400 rounded-xl text-sm border border-gray-500/30 animate-pulse">
                                                    <i className="fas fa-plus mr-1"></i>
                                                    {note.tags.length - 4} more
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Enhanced Footer with Better Date/Time Display */}
                                <div className="border-t border-white/20 pt-4 relative z-10 mt-auto">
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="flex items-center space-x-6">
                                            {/* Author Section */}
                                            <div className="flex items-center group-hover:text-yellow-300 transition-colors duration-300">
                                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center mr-2 transition-all duration-300 group-hover:scale-110">
                                                    <i className="fas fa-user text-blue-400 text-xs"></i>
                                                </div>
                                                <span className="font-semibold">{note.authorName || 'Admin User'}</span>
                                            </div>
                                            
                                            {/* Enhanced Date/Time Display */}
                                            <div className="flex items-center group-hover:text-orange-300 transition-colors duration-300">
                                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center mr-2 transition-all duration-300 group-hover:scale-110">
                                                    <i className="fas fa-clock text-orange-400 text-xs"></i>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-semibold leading-tight">
                                                        {note.lastModified ? new Date(note.lastModified).toLocaleDateString('en-US', { 
                                                            month: 'short', 
                                                            day: 'numeric', 
                                                            year: 'numeric' 
                                                        }) : 'Unknown'}
                                                    </span>
                                                    <span className="text-xs text-gray-500 leading-tight">
                                                        {note.lastModified ? new Date(note.lastModified).toLocaleTimeString('en-US', { 
                                                            hour: '2-digit', 
                                                            minute: '2-digit' 
                                                        }) : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Word Count & Reading Time */}
                                        {/* <div className="flex items-center space-x-4 text-gray-500">
                                            <div className="flex items-center">
                                                <i className="fas fa-align-left mr-1 text-xs"></i>
                                                <span className="text-xs">
                                                    {note.content ? note.content.replace(/<[^>]*>/g, '').split(' ').filter(word => word.length > 0).length : 0} words
                                                </span>
                                            </div>
                                            <div className="flex items-center">
                                                <i className="fas fa-eye mr-1 text-xs"></i>
                                                <span className="text-xs">
                                                    {note.content ? Math.max(1, Math.ceil(note.content.replace(/<[^>]*>/g, '').split(' ').length / 200)) : 0}min read
                                                </span>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>

                                {/* Hover Overlay for Click-to-Edit */}
                                <div 
                                    className="absolute inset-0 z-30 cursor-pointer opacity-0 group-hover:opacity-5 bg-gradient-to-br from-yellow-400 to-orange-400 transition-opacity duration-300 rounded-3xl"
                                    onClick={() => handleEditNote(note)}
                                ></div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminNotes;
